import {
    collection,
    getDocs,
    query,
    where,
    addDoc,
    updateDoc,
    doc,
    Timestamp,
    orderBy,
    getFirestore,
    increment,
    setDoc
} from 'firebase/firestore';
import { getAuth } from 'firebase/auth';

interface PageVisit {
    page: string;
    timestamp: Timestamp;
    sessionId: string;
    deviceType: 'desktop' | 'mobile' | 'tablet';
    country?: string;
    referrer?: string;
    isNewUser: boolean;
    timeOnPage?: number;
}

interface VisitorStats {
    visitors: number[];
    pageViews: number[];
    labels: string[];
}

interface DeviceData {
    desktop: number;
    mobile: number;
    tablet: number;
}

interface PageVisitsData {
    labels: string[];
    data: number[];
}

interface GeographyData {
    labels: string[];
    data: number[];
}

interface ContentEngagementData {
    labels: string[];
    data: number[];
}

interface BounceRateData {
    returning: number;
    new: number;
    bounce: number;
}

export interface AnalyticsData {
    visitorStats: VisitorStats;
    deviceData: DeviceData;
    pageVisitsData: PageVisitsData;
    geographyData: GeographyData;
    contentEngagementData: ContentEngagementData;
    bounceRateData: BounceRateData;
}

// Track a page visit
export const trackPageVisit = async (pageData: Omit<PageVisit, 'timestamp'>) => {
    const db = getFirestore();
    try {
        await addDoc(collection(db, 'pageVisits'), {
            ...pageData,
            timestamp: Timestamp.now()
        });
    } catch (error) {
        console.error('Error tracking page visit:', error);
    }
};

// Get visitors and page views data for the specified time period
export const getVisitorStats = async (period: '7days' | '30days' | '90days' | 'year') => {
    const db = getFirestore();
    const now = new Date();
    let startDate: Date;
    let labels: string[] = [];

    // Calculate start date based on period
    switch (period) {
        case '7days':
            startDate = new Date(now);
            startDate.setDate(now.getDate() - 7);
            labels = Array.from({ length: 7 }, (_, i) => {
                const date = new Date(now);
                date.setDate(now.getDate() - (6 - i));
                return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
            });
            break;
        case '30days':
            startDate = new Date(now);
            startDate.setDate(now.getDate() - 30);
            labels = Array.from({ length: 30 }, (_, i) => {
                const date = new Date(now);
                date.setDate(now.getDate() - (29 - i));
                return i % 5 === 0 ? date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '';
            }).filter(label => label !== '');
            break;
        case '90days':
            startDate = new Date(now);
            startDate.setDate(now.getDate() - 90);
            labels = Array.from({ length: 3 }, (_, i) => {
                const date = new Date(now);
                date.setMonth(now.getMonth() - 2 + i);
                return date.toLocaleDateString('en-US', { month: 'short' });
            });
            break;
        case 'year':
        default:
            startDate = new Date(now);
            startDate.setFullYear(now.getFullYear() - 1);
            labels = Array.from({ length: 12 }, (_, i) => {
                const date = new Date(now);
                date.setMonth(now.getMonth() - 11 + i);
                return date.toLocaleDateString('en-US', { month: 'short' });
            });
            break;
    }

    try {
        const q = query(
            collection(db, 'pageVisits'),
            where('timestamp', '>=', Timestamp.fromDate(startDate)),
            orderBy('timestamp', 'asc')
        );

        const snapshot = await getDocs(q);
        const visits = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as PageVisit & { id: string }));

        // Process data for visitors and pageviews based on period
        const uniqueVisitors = new Set<string>();
        const pageViewsData: { [key: string]: number } = {};
        const visitorsData: { [key: string]: number } = {};

        // Initialize data structure based on labels
        labels.forEach(label => {
            visitorsData[label] = 0;
            pageViewsData[label] = 0;
        });

        visits.forEach(visit => {
            const visitDate = visit.timestamp.toDate();
            let labelKey: string;

            if (period === '7days') {
                labelKey = visitDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
            } else if (period === '30days') {
                labelKey = visitDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                // Find the closest label
                const closestLabel = labels.find(l => l.includes(visitDate.toLocaleDateString('en-US', { month: 'short' })));
                labelKey = closestLabel || labelKey;
            } else if (period === '90days') {
                labelKey = visitDate.toLocaleDateString('en-US', { month: 'short' });
            } else {
                labelKey = visitDate.toLocaleDateString('en-US', { month: 'short' });
            }

            if (labels.includes(labelKey)) {
                pageViewsData[labelKey]++;

                if (!uniqueVisitors.has(visit.sessionId)) {
                    uniqueVisitors.add(visit.sessionId);
                    visitorsData[labelKey]++;
                }
            }
        });

        return {
            visitors: labels.map(label => visitorsData[label] || 0),
            pageViews: labels.map(label => pageViewsData[label] || 0),
            labels
        };
    } catch (error) {
        console.error('Error fetching visitor stats:', error);
        // Return empty data on error
        return {
            visitors: labels.map(() => 0),
            pageViews: labels.map(() => 0),
            labels
        };
    }
};

// Get device usage data
export const getDeviceData = async () => {
    const db = getFirestore();
    try {
        const q = query(
            collection(db, 'pageVisits'),
            where('timestamp', '>=', Timestamp.fromDate(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)))
        );

        const snapshot = await getDocs(q);
        const visits = snapshot.docs.map(doc => doc.data() as PageVisit);

        const uniqueSessionIds = new Set<string>();
        const deviceCounts = { desktop: 0, mobile: 0, tablet: 0 };

        visits.forEach(visit => {
            // Count each session only once for device stats
            const sessionKey = `${visit.sessionId}-${visit.deviceType}`;
            if (!uniqueSessionIds.has(sessionKey)) {
                uniqueSessionIds.add(sessionKey);
                deviceCounts[visit.deviceType]++;
            }
        });

        return deviceCounts;
    } catch (error) {
        console.error('Error fetching device data:', error);
        return { desktop: 0, mobile: 0, tablet: 0 };
    }
};

// Get page visits data
export const getPageVisitsData = async () => {
    const db = getFirestore();
    try {
        const q = query(
            collection(db, 'pageVisits'),
            where('timestamp', '>=', Timestamp.fromDate(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)))
        );

        const snapshot = await getDocs(q);
        const visits = snapshot.docs.map(doc => doc.data() as PageVisit);

        const pageCounts: { [key: string]: number } = {};

        visits.forEach(visit => {
            pageCounts[visit.page] = (pageCounts[visit.page] || 0) + 1;
        });

        // Sort pages by visit count
        const sortedPages = Object.entries(pageCounts)
            .sort((a, b) => b[1] - a[1])
            .slice(0, 6); // Get top 6 pages

        return {
            labels: sortedPages.map(([page]) => page),
            data: sortedPages.map(([, count]) => count)
        };
    } catch (error) {
        console.error('Error fetching page visits data:', error);
        return { labels: [], data: [] };
    }
};

// Get geography data
export const getGeographyData = async () => {
    const db = getFirestore();
    try {
        const q = query(
            collection(db, 'pageVisits'),
            where('timestamp', '>=', Timestamp.fromDate(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)))
        );

        const snapshot = await getDocs(q);
        const visits = snapshot.docs.map(doc => doc.data() as PageVisit);

        const countryCounts: { [key: string]: number } = {};
        const sessionCountries = new Map<string, string>();

        visits.forEach(visit => {
            if (visit.country && !sessionCountries.has(visit.sessionId)) {
                sessionCountries.set(visit.sessionId, visit.country);
                countryCounts[visit.country] = (countryCounts[visit.country] || 0) + 1;
            }
        });

        // Sort countries by visit count
        const sortedCountries = Object.entries(countryCounts)
            .sort((a, b) => b[1] - a[1]);

        // Get top 6 countries and group the rest as "Others"
        const topCountries = sortedCountries.slice(0, 6);
        const otherCountriesCount = sortedCountries.slice(6).reduce((sum, [, count]) => sum + count, 0);

        const labels = topCountries.map(([country]) => country);
        const data = topCountries.map(([, count]) => count);

        if (otherCountriesCount > 0) {
            labels.push('Others');
            data.push(otherCountriesCount);
        }

        return { labels, data };
    } catch (error) {
        console.error('Error fetching geography data:', error);
        return { labels: [], data: [] };
    }
};

// Get content engagement data
export const getContentEngagementData = async () => {
    const db = getFirestore();
    try {
        const q = query(
            collection(db, 'pageVisits'),
            where('timestamp', '>=', Timestamp.fromDate(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)))
        );

        const snapshot = await getDocs(q);
        const visits = snapshot.docs.map(doc => doc.data() as PageVisit);

        // Define content categories and their corresponding pages
        const contentCategories = {
            'Projects': ['/projects', '/project/'],
            'Experience': ['/resume'],
            'Education': ['/resume'],
            'Publications': ['/publications']
        };

        const categoryEngagement: { [key: string]: { visits: number, timeSpent: number } } = {};

        // Initialize categories
        Object.keys(contentCategories).forEach(category => {
            categoryEngagement[category] = { visits: 0, timeSpent: 0 };
        });

        // Process visits
        visits.forEach(visit => {
            Object.entries(contentCategories).forEach(([category, paths]) => {
                if (paths.some(path => visit.page.includes(path))) {
                    categoryEngagement[category].visits++;
                    if (visit.timeOnPage) {
                        categoryEngagement[category].timeSpent += visit.timeOnPage;
                    }
                }
            });
        });

        // Calculate engagement score (based on visits and time spent)
        const maxVisits = Math.max(...Object.values(categoryEngagement).map(v => v.visits));
        const maxTimeSpent = Math.max(...Object.values(categoryEngagement).map(v => v.timeSpent));

        const engagementScores: { [key: string]: number } = {};

        Object.entries(categoryEngagement).forEach(([category, data]) => {
            const visitScore = maxVisits > 0 ? (data.visits / maxVisits) * 50 : 0;
            const timeScore = maxTimeSpent > 0 ? (data.timeSpent / maxTimeSpent) * 50 : 0;
            engagementScores[category] = Math.round(visitScore + timeScore);
        });

        return {
            labels: Object.keys(engagementScores),
            data: Object.values(engagementScores)
        };
    } catch (error) {
        console.error('Error fetching content engagement data:', error);
        return { labels: [], data: [] };
    }
};

// Get bounce rate data
export const getBounceRateData = async () => {
    const db = getFirestore();
    try {
        const q = query(
            collection(db, 'pageVisits'),
            where('timestamp', '>=', Timestamp.fromDate(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)))
        );

        const snapshot = await getDocs(q);
        const visits = snapshot.docs.map(doc => doc.data() as PageVisit);

        // Count visits per session
        const sessionsMap = new Map<string, { count: number, isNew: boolean }>();

        visits.forEach(visit => {
            if (!sessionsMap.has(visit.sessionId)) {
                sessionsMap.set(visit.sessionId, { count: 1, isNew: visit.isNewUser });
            } else {
                const session = sessionsMap.get(visit.sessionId)!;
                session.count++;
                sessionsMap.set(visit.sessionId, session);
            }
        });

        // Calculate metrics
        const bounceCount = Array.from(sessionsMap.values()).filter(session => session.count === 1).length;
        const newCount = Array.from(sessionsMap.values()).filter(session => session.isNew && session.count > 1).length;
        const returningCount = Array.from(sessionsMap.values()).filter(session => !session.isNew && session.count > 1).length;

        return {
            returning: returningCount,
            new: newCount,
            bounce: bounceCount
        };
    } catch (error) {
        console.error('Error fetching bounce rate data:', error);
        return { returning: 0, new: 0, bounce: 0 };
    }
};

// Get all analytics data at once
export const getAllAnalyticsData = async (period: '7days' | '30days' | '90days' | 'year'): Promise<AnalyticsData> => {
    const [
        visitorStats,
        deviceData,
        pageVisitsData,
        geographyData,
        contentEngagementData,
        bounceRateData
    ] = await Promise.all([
        getVisitorStats(period),
        getDeviceData(),
        getPageVisitsData(),
        getGeographyData(),
        getContentEngagementData(),
        getBounceRateData()
    ]);

    return {
        visitorStats,
        deviceData,
        pageVisitsData,
        geographyData,
        contentEngagementData,
        bounceRateData
    };
};

// Initialize session tracking - called once when user visits the site
export const initializeTracking = () => {
    const sessionId = generateSessionId();
    const isNewUser = !localStorage.getItem('portfolioVisitor');

    // Store session ID for this browsing session
    sessionStorage.setItem('portfolioSessionId', sessionId);

    // Mark as returning visitor for future visits
    localStorage.setItem('portfolioVisitor', 'true');

    return { sessionId, isNewUser };
};

// Generate a unique session ID
const generateSessionId = (): string => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
        const r = Math.random() * 16 | 0, v = c == 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
    });
}; 